import { useTranslation } from '@rhino-project/core/i18n';
import {
  usePasskeys,
  useRegisterPasskey,
  useRemovePasskey,
  usePasskeySignal
} from '@rhino-project/core/queries';
import { Button, Card, CardContent, Typography } from '@heroui/react';

interface PasskeyRow {
  id: number;
  label: string;
  transports: string[];
  last_used_at: string | null;
  created_at: string;
  can_remove: boolean;
}

interface PasskeysPayload {
  passkeys: PasskeyRow[];
}

export const PasskeysSettings = () => {
  const { t, locale } = useTranslation();
  // Keep the provider's stored passkeys in sync with the server on load and after
  // add/remove (self-managing; no visible UI).
  usePasskeySignal();

  // Data is prefetched by the sign-in route loader (prefetchPasskeys), so this
  // useQuery hits a warm cache and renders without a loading spinner. The
  // `data ?? { passkeys: [] }` fallback keeps a non-prefetched mount rendering
  // an empty state.
  const { data } = usePasskeys();
  const { mutate: register, isPending: registering } = useRegisterPasskey();
  const { mutate: remove, isPending: removing } = useRemovePasskey();

  const payload = (data ?? { passkeys: [] }) as PasskeysPayload;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <Typography.Heading level={4} weight="medium">
          {t('rhino.frontend.messages.passkeys')}
        </Typography.Heading>
        <Button
          size="sm"
          isDisabled={registering}
          onPress={() => register(undefined)}
        >
          {t('rhino.frontend.messages.add_a_passkey')}
        </Button>
      </div>

      {payload.passkeys.length === 0 && (
        <span className="text-sm text-muted">
          {t('rhino.frontend.messages.no_passkeys_yet')}
        </span>
      )}

      {payload.passkeys.map((passkey) => (
        <Card key={passkey.id}>
          <CardContent className="flex flex-row items-center justify-between">
            <div className="flex flex-col gap-1">
              <span className="font-medium">{passkey.label}</span>
              <span className="text-sm text-muted">
                {passkey.last_used_at
                  ? t('rhino.frontend.messages.last_used_value', {
                      value1: new Date(passkey.last_used_at).toLocaleDateString(
                        locale.replaceAll('_', '-')
                      )
                    })
                  : t('rhino.frontend.messages.added_value', {
                      value1: new Date(passkey.created_at).toLocaleDateString(
                        locale.replaceAll('_', '-')
                      )
                    })}
              </span>
            </div>
            <Button
              size="sm"
              variant="danger-soft"
              isDisabled={removing || !passkey.can_remove}
              onPress={() => remove(passkey.id)}
            >
              {t('rhino.frontend.messages.remove')}
            </Button>
          </CardContent>
        </Card>
      ))}
    </section>
  );
};
